/**
 * AnalyticsNav — thin sub-navigation shell mounted at /analytics.
 *
 * Analytics is the sole top-level Ops nav item (see Layout.jsx); Daily
 * Report is reachable only through this internal "Analytics | Daily
 * Report" tab strip. App.js wraps this component (and its <Outlet />) in
 * a single shared SensitivePageGate, so unlocking once covers both tabs
 * while this combined area stays mounted; switching between them does not
 * re-prompt. Deep-linking either /analytics or /analytics/daily-report
 * still fails closed behind that shared gate.
 */
import { NavLink, Outlet } from "react-router-dom";
import { prefetchDailyReport } from "@/features/daily-report/dailyReportQueryClient";
import { getSensitivePagesToken } from "@/lib/sensitivePages";

function prefetchDailyReportChunk() {
  void import("@/features/daily-report/DailyReportV2Root");
}

function getTodayIsoDate() {
  return new Date().toISOString().split("T")[0];
}

function warmDailyReport() {
  prefetchDailyReportChunk();
  if (!getSensitivePagesToken()) return;
  void prefetchDailyReport(getTodayIsoDate());
}

const TABS = [
  { path: "/analytics", label: "Analytics", end: true },
  { path: "/analytics/daily-report", label: "Daily Report", end: false, onHover: warmDailyReport },
];

export default function AnalyticsNav() {
  return (
    <div data-testid="analytics-nav">
      <nav className="flex gap-2 mb-4" aria-label="Analytics sections" data-testid="analytics-sub-nav">
        {TABS.map((tab) => (
          <NavLink
            key={tab.path}
            to={tab.path}
            end={tab.end}
            onMouseEnter={tab.onHover}
            onFocus={tab.onHover}
            className={({ isActive }) =>
              `px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                isActive
                  ? "bg-[#EE2677] text-white shadow-sm"
                  : "bg-white text-[#2D2D2D] border border-[#EBB3A9]/50 hover:bg-[#FFF3EE]"
              }`
            }
            data-testid={`analytics-tab-${tab.label.toLowerCase().replace(/\s+/g, "-")}`}
          >
            {tab.label}
          </NavLink>
        ))}
      </nav>
      <Outlet />
    </div>
  );
}
